// Soundex algorithm for fuzzy phonetic matching
// Useful for finding places even with spelling mistakes

export function soundex(s: string): string {
  if (!s) return ''
  
  const str = s.toUpperCase().replace(/[^A-Z]/g, '')
  if (str.length === 0) return ''
  
  // Keep first letter
  let code = str[0]
  
  // Soundex mapping
  const mapping: { [key: string]: string } = {
    'B': '1', 'F': '1', 'P': '1', 'V': '1',
    'C': '2', 'G': '2', 'J': '2', 'K': '2', 'Q': '2', 'S': '2', 'X': '2', 'Z': '2',
    'D': '3', 'T': '3',
    'L': '4',
    'M': '5', 'N': '5',
    'R': '6'
  }
  
  let prevCode = mapping[str[0]] || '0'
  
  for (let i = 1; i < str.length; i++) {
    const currentCode = mapping[str[i]] || '0'
    
    // Ignore vowels and duplicates
    if (currentCode !== '0' && currentCode !== prevCode) {
      code += currentCode
    }
    
    prevCode = currentCode
    
    if (code.length === 4) break
  }
  
  // Pad with zeros to length 4
  return (code + '0000').substring(0, 4)
}

// Calculate similarity between two strings using Soundex
export function soundexMatch(str1: string, str2: string): boolean {
  return soundex(str1) === soundex(str2)
}

// Levenshtein distance for fuzzy string matching
export function levenshteinDistance(str1: string, str2: string): number {
  const s1 = str1.toLowerCase()
  const s2 = str2.toLowerCase()
  
  const matrix: number[][] = []
  
  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i]
  }
  
  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j
  }
  
  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        )
      }
    }
  }
  
  return matrix[s2.length][s1.length]
}

// Calculate similarity score (0-1, where 1 is exact match)
export function similarityScore(str1: string, str2: string): number {
  const maxLength = Math.max(str1.length, str2.length)
  if (maxLength === 0) return 1
  
  const distance = levenshteinDistance(str1, str2)
  return 1 - (distance / maxLength)
}

// Fuzzy search: combine exact, prefix, soundex, and Levenshtein matching
export function fuzzyMatch(searchTerm: string, targetName: string): {
  matches: boolean
  score: number
  matchType: 'exact' | 'prefix' | 'contains' | 'soundex' | 'fuzzy' | 'none'
} {
  const search = searchTerm.toLowerCase().trim()
  const target = targetName.toLowerCase().trim()
  
  // Exact match
  if (target === search) {
    return { matches: true, score: 1.0, matchType: 'exact' }
  }
  
  // Prefix match
  if (target.startsWith(search)) {
    return { matches: true, score: 0.9, matchType: 'prefix' }
  }
  
  // Contains match
  if (target.includes(search)) {
    return { matches: true, score: 0.8, matchType: 'contains' }
  }
  
  // Fuzzy match with Levenshtein distance (more lenient threshold)
  const similarity = similarityScore(search, target)
  if (similarity >= 0.6) {
    return { matches: true, score: similarity * 0.7, matchType: 'fuzzy' }
  }
  
  // Word-by-word matching for multi-word queries
  const searchWords = search.split(/\s+/)
  const targetWords = target.split(/\s+/)
  
  // Check if all search words loosely match target words
  const allWordsMatch = searchWords.every(searchWord => {
    return targetWords.some(targetWord => {
      // Prefix match
      if (targetWord.startsWith(searchWord)) return true
      // Contains match
      if (targetWord.includes(searchWord)) return true
      // Similarity match (very lenient for individual words)
      const wordSimilarity = similarityScore(searchWord, targetWord)
      return wordSimilarity >= 0.5
    })
  })
  
  if (allWordsMatch && searchWords.length > 0) {
    // Calculate average word similarity
    let totalSimilarity = 0
    searchWords.forEach(searchWord => {
      let bestWordMatch = 0
      targetWords.forEach(targetWord => {
        const wordSim = similarityScore(searchWord, targetWord)
        if (wordSim > bestWordMatch) bestWordMatch = wordSim
      })
      totalSimilarity += bestWordMatch
    })
    const avgSimilarity = totalSimilarity / searchWords.length
    return { matches: true, score: avgSimilarity * 0.65, matchType: 'fuzzy' }
  }
  
  return { matches: false, score: 0, matchType: 'none' }
}
